<script setup lang="ts">
import { useCharts } from './Chart.composables'
import type { Chart } from './Chart.types'
import Loader from '@/components/Loader/Loader.vue'

const props = withDefaults(defineProps<Chart>(), {
  type: 'line',
  height: '400px',
  title: '',
  variant: 'default',
})

const {
  ChartComponent,
  normalizedChartData,
  normalizedChartOptions,
  loadChartResources,
} = useCharts()

watch(
  () => [props.type, props.data, props.options],
  () => {
    void loadChartResources(props.type, props.data, props.options)
  },
  { immediate: true },
)
</script>

<template>
  <div class="chart">
    <h3
      v-if="props.title"
      class="chart__title"
    >
      {{ props.title }}
    </h3>

    <div
      :class="['chart__content', `chart__content--${props.variant}`]"
      :style="{ height: props.height }"
    >
      <div
        v-if="ChartComponent && normalizedChartData && normalizedChartOptions"
        :class="['chart__surface', `chart__surface--${props.variant}`]"
      >
        <component
          :is="ChartComponent"
          :data="normalizedChartData"
          :options="normalizedChartOptions"
          :height="props.height"
        />
      </div>

      <div
        v-else
        class="chart__loader"
      >
        <Loader v-show="true" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.chart {
  &__title {
    @include h9;
    margin-bottom: 3.2rem;
    text-align: center;
  }

  &__content {
    &--scrollable {
      overflow-x: visible;
      overflow-y: hidden;
      -webkit-overflow-scrolling: touch;
    }
  }

  &__surface {
    position: relative;
    height: 100%;

    &--scrollable {
      min-width: 96rem;
    }
  }
  
  &__loader {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    margin: auto;
  }

  canvas {
    max-width: 100%;
  }

  @include atXS {
    &__surface--scrollable {
      min-width: 96rem;
    }
  }

  @include atS {
    &__content--scrollable {
      overflow-x: auto;
    }

    &__surface--scrollable {
      min-width: 100%;
    }
  }

  @include atM {
    &__title {
      @include h8;
    }
  }
}
</style>
